import { DataUtility as _ } from "@utils/DataUtility";
import {
  ForkEffect,
  put,
  select,
  takeLatest,
  call,
  takeEvery,
} from "redux-saga/effects";
import { IFormServer, IRulesMap, IWorkflowMap } from "../forms/interface";
import { transformFormDataServerToClient } from "../forms/utils";
import {
  ChangeFieldPositionFailureAction,
  ChangeFieldPositionSuccessAction,
  formBuilderActions,
  setActiveFormFailureAction,
  setActiveFormSuccessAction,
  setFieldToShowAndHideAction,
  setPageToShowAndHideAction,
} from "./actions";
import {
  IChangeFieldPositionAction,
  IForm,
  ISetActiveFormAction,
  IValidateFieldRule,
  IFieldsToHIdeAndShow,
  IAutoSaveFormLocallyAction,
  IPageToHIdeAndShow,
  submisionType,
  IGetSignatureFormFilesAction,
  IUpdateFormLockData,
  IFetchApplicationPage,
} from "./interface";
import {
  getActiveFormDataSelector,
  getActiveSubmissionType,
  getUpdatedFormMode,
} from "./selectors";
import {
  changeFieldPosition,
  removeDuplicates,
  transformActionDataToSubmissionData,
} from "./utils";
import {
  fetchRulesMapSelector,
  fetchWorkflowMapSelector,
} from "@common/redux/forms/selectors";
import {
  getActiveFormFieldSubmissionFieldSubmissionMapSelector,
  getdraftvalue,
  getFormSubmissionRespondedByData,
  getUniversalStorageUnit,
} from "@common/redux/formSubmission/selectors";
import {
  IValidateFormRules,
  valiDateFormRule,
  valiDateWorkflow,
} from "@presentation/FormBuilder/components/utils";
import {
  Events,
  IFieldSubmissionMap,
  IFormFieldSubmission,
  IRespondedBySubmissionMap,
} from "@common/redux/formSubmission/interface";
import { bulkUpdateSubmissionValuesForValidation } from "../formSubmission/actions";
import { IProfileMap } from "../profile/interface";
import { getProfileMapSelector } from "../profile/selectors";
import { getActiveOrganizationDataSelector } from "../admin/selector";
import { IAttribute } from "../admin/interface";
import { FormsRepository } from "@common/domain/repositories/FormsRepository";
import { FormModes } from "@erelego/design-system/interface/Forms/formBuilderInterface";

function* getPageDataFromApplication(action: IFetchApplicationPage) {
  const { onSuccess, onFailure, applicationId } = action.payload;
  try {
    const response = yield call(FormsRepository.getPageData, applicationId);
    onSuccess(response);
  } catch (e) {
    onFailure(e.message);
  }
}

function* setActiveFormSaga(action: ISetActiveFormAction) {
  try {
    const formData: IFormServer = _.cloneDeep(action.payload.formData);
    const formDataClient = transformFormDataServerToClient(formData);
    yield put(setActiveFormSuccessAction(formDataClient));
  } catch (e) {
    yield put(setActiveFormFailureAction(e.message));
  }
}
function* changeFieldPositionSaga(action: IChangeFieldPositionAction) {
  try {
    const activeFormData: IForm = yield select(getActiveFormDataSelector);
    const { draggedFieldPosition, dropFieldPosition, dropFormId, dragFormId } =
      action.payload || {};
    const updatedActiveFormData: IForm = changeFieldPosition(
      activeFormData,
      draggedFieldPosition,
      dropFieldPosition,
      dropFormId,
      dragFormId
    );
    yield put(ChangeFieldPositionSuccessAction(updatedActiveFormData));
  } catch (e) {
    yield put(ChangeFieldPositionFailureAction(e.message));
  }
}

function* validateFieldRuleSaga(action: IValidateFieldRule) {
  let submissionMapData = {};
  try {
    const fieldId = action.payload.fieldId;
    const fieldSubmissionMapProps = action.payload.submissionMap;
    const activeSubmissionType: submisionType = yield select(
      getActiveSubmissionType
    );
    const submissionType: submisionType =
      activeSubmissionType || action.payload.submissionType;
    let event: Events = Events.CREATEDEDITED;
    const isDraftSubmission: boolean = yield select(getdraftvalue);
    if (isDraftSubmission) {
      event = Events.CREATED;
    } else {
      if (submissionType == submisionType.SUBMISSION) {
        event = Events.CREATED;
      } else if (submissionType == submisionType.RESUBMISSION) {
        event = Events.EDITED;
      }
    }

    const rulesMap: IRulesMap = yield select(fetchRulesMapSelector);
    const workFlowMap: IWorkflowMap = yield select(fetchWorkflowMapSelector);
    const respondedByData: IRespondedBySubmissionMap = yield select(
      getFormSubmissionRespondedByData
    );
    const activeFormData: IForm = yield select(getActiveFormDataSelector);
    const formMode: FormModes = yield select(getUpdatedFormMode);
    const attributes: IAttribute = yield select(
      getActiveOrganizationDataSelector
    );
    const fieldSubmissionMap: IFieldSubmissionMap = yield select(
      getActiveFormFieldSubmissionFieldSubmissionMapSelector
    );
    if (fieldSubmissionMapProps) {
      submissionMapData = {
        ...submissionMapData,
        ...fieldSubmissionMapProps,
        ...fieldSubmissionMap,
      };
    } else {
      submissionMapData = {
        ...submissionMapData,
        ...fieldSubmissionMap,
      };
    }
    const formRules = rulesMap[activeFormData?.localId];
    const fieldProfileMap: IProfileMap = yield select(getProfileMapSelector);
    const {
      fieldsToHideAndShow,
      pageToHideAndShow,
      submissionAction,
      valuesToUpdate,
    }: IValidateFormRules = valiDateFormRule(
      formRules,
      submissionMapData,
      activeFormData,
      fieldProfileMap
    );
    const workFlowList = workFlowMap[activeFormData?.localId];
    const universalStorage: any =yield select(getUniversalStorageUnit);
    const updatedAttributes = {
      ...attributes,
      ...universalStorage?.userAttributes,
    };
    const {
      fieldsToHideAndShow: workflowFieldsToHideAndShow,
      pageToHideAndShow: workflowpageToHideAndShow,
      valuesToUpdate: workflowValuesToUpdate,
      submissionAction: workflowSubmissionAction,
    }: IValidateFormRules = valiDateWorkflow(
      workFlowList,
      submissionMapData,
      activeFormData,
      event,
      fieldProfileMap,
      respondedByData,
      updatedAttributes,
      isDraftSubmission
    );
    const updatedFieldsToHideAndShow: IFieldsToHIdeAndShow = {
      hide: removeDuplicates([
        ...fieldsToHideAndShow.hide,
        ...workflowFieldsToHideAndShow?.hide,
      ]),
      show: removeDuplicates([
        ...fieldsToHideAndShow.show,
        ...workflowFieldsToHideAndShow?.show,
      ]),
      disabled: [
        ...fieldsToHideAndShow?.disabled,
        ...workflowFieldsToHideAndShow?.disabled,
      ],
    };

    const updatedPageToHideAndShow: IPageToHIdeAndShow = {
      skip: removeDuplicates([
        ...pageToHideAndShow.skip,
        ...workflowpageToHideAndShow?.skip,
      ]),
      show: removeDuplicates([
        ...pageToHideAndShow.show,
        ...workflowpageToHideAndShow?.show,
      ]),
      hide: removeDuplicates([
        ...pageToHideAndShow.hide,
        ...workflowpageToHideAndShow?.hide,
      ]),
      elseSkip: removeDuplicates([
        ...pageToHideAndShow.elseSkip,
        ...workflowpageToHideAndShow?.elseSkip,
      ]),
    };
  
    if (formMode !== FormModes.responseView) {
      const bulkUpdateData: IFormFieldSubmission[] =
        transformActionDataToSubmissionData([...valuesToUpdate, ...workflowValuesToUpdate],
          submissionMapData,
          fieldProfileMap,
          attributes,
          activeFormData?.localId,
          updatedFieldsToHideAndShow?.hide,
          updatedPageToHideAndShow.hide,
          activeFormData?.fieldsMap
        );
      yield put(bulkUpdateSubmissionValuesForValidation([], bulkUpdateData));
    }
    yield put(setFieldToShowAndHideAction(updatedFieldsToHideAndShow));
    yield put(setPageToShowAndHideAction(updatedPageToHideAndShow));
    // if ((submissionAction && submissionAction?.value) || (workflowSubmissionAction && workflowSubmissionAction?.value)) {
    //   const setModalPayload: IModalData = {
    //     content: submissionAction?.value || workflowSubmissionAction?.value,
    //     type: submissionAction?.actionType || workflowSubmissionAction?.actionType,
    //     visibility: true,
    //   }
    //   yield put(setModalData(setModalPayload));
    // }
    // yield put(setFieldToShowAndHideAction(updatedFieldsToHideAndShow));
    // yield put(setPageToShowAndHideAction(updatedPageToHideAndShow));
  } catch (e) {
    console.log(e, "validate Field Rule error");
  }
}

function* autoSaveFormLocallySaga(action: IAutoSaveFormLocallyAction) {
  const formIsSaved = action?.payload?.isSaved;
  const activeFormBuilder: IForm = yield select(getActiveFormDataSelector);
  const localId = activeFormBuilder?.localId;

  if (localId && localId !== "parentId") {
    const autoSavedFormsMap = JSON.parse(
      localStorage.getItem("autoSavedFormsMap") ?? "{}"
    );
    autoSavedFormsMap[localId] = activeFormBuilder;
    localStorage.setItem(
      "autoSavedFormsMap",
      JSON.stringify(autoSavedFormsMap)
    );
  }
}

function* getSignatureFormFiles(action: IGetSignatureFormFilesAction) {
  const { onSuccessFileFetch, onFailureFileFetch, signatureID } =
    action.payload;
  try {
    const response = yield call(
      FormsRepository.getSignatureDetailsById,
      signatureID
    );
    const fetchFileSuccessPayload = {
      file: response.data,
      signatureID: signatureID,
    };
    onSuccessFileFetch(fetchFileSuccessPayload);
  } catch (e) {
    onFailureFileFetch(e.message);
  }
}

function* updateFormLockDataSaga(action: IUpdateFormLockData) {
  const formLockData = action.payload?.formLockData;
  const formId = formLockData?.formId;
  try {
    const response = yield call(
      FormsRepository.updateFormLockData,
      formId,
      formLockData
    );
    const formLockSuccessPayload = {
      successResponse: response.data,
    };
  } catch (e) {
    console.log(e.message);
  }
}

export function* watchFormBuilder(): Generator<ForkEffect> {
  yield takeLatest(
    formBuilderActions.FETCH_APPLICATION_PAGE,
    getPageDataFromApplication
  );
  yield takeLatest(formBuilderActions.SET_ACTIVE_FORM, setActiveFormSaga);
  yield takeLatest(
    formBuilderActions.CHANGE_FIELD_POSITION,
    changeFieldPositionSaga
  );
  yield takeLatest(
    formBuilderActions.VALIDATE_FIELD_RULE,
    validateFieldRuleSaga
  );
  yield takeLatest(
    formBuilderActions.AUTO_SAVE_FORM_LOCALLY,
    autoSaveFormLocallySaga
  );
  yield takeEvery(
    formBuilderActions.GET_SIGNATURE_FORM_FILES,
    getSignatureFormFiles
  );
  yield takeEvery(
    formBuilderActions.UPDATE_FORM_LOCK_DATA,
    updateFormLockDataSaga
  );
}
